import type {
  SearchSystemPackageVersionsV2MutationRequestApi,
  SearchFilterApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import { searchFilterFilterType, searchOperator } from '@ingenico/api-sdk-device-management-private-v2';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type {
  AdvancedSearchApi,
  AdvancedSearchField,
  DatagridQuickFilter,
  DataGridSimpleQuickFilter,
  DefaultQuickFilter,
} from '@ingenico/react-ui/advanced-search';
import { DatagridQuickFilterTypeEnum, useQuickFilters } from '@ingenico/react-ui/advanced-search';
import { isEmpty, toString } from 'lodash-es';
import type { RefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApplicationStatusEnum } from '../components/application-list/ApplicationList.model';

export interface VersionQuickFilter {
  readonly filterRefApi: RefObject<AdvancedSearchApi>;
  readonly isLoadingQuickFilters: boolean;
  readonly fields: AdvancedSearchField[];
  readonly getLabelQuickFilterLabel: (name: string) => string;
  readonly overrideDefaultFilterTypes: (
    field: string,
    currentType: DatagridQuickFilterTypeEnum,
  ) => DatagridQuickFilterTypeEnum;
  readonly quickFilterCall: () => Promise<
    {
      readonly label: string;
      readonly value: string;
    }[]
  >;
  readonly checkAllowReset: (filter?: string) => boolean;
  readonly handleQuickFilterChange: (quickFilters: DatagridQuickFilter[]) => void;
  readonly searchVersionQuickFilter: SearchFilterApi[];
}

export interface VersionQuickFilterProps {
  readonly labelPrefix?: string;
}
export function useVersionQuickFilterHook(props?: VersionQuickFilterProps): VersionQuickFilter {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON]);
  const [selectedQuickFilterValue, setSelectedQuickFilterValue] = useState<string>();

  const searchVersionQuickFilter = useMemo<SearchSystemPackageVersionsV2MutationRequestApi['filters']>(
    () =>
      selectedQuickFilterValue
        ? [
            {
              filterType: searchFilterFilterType.array,
              name: 'status',
              operator: searchOperator.in,
              value: JSON.stringify([selectedQuickFilterValue]),
            },
          ]
        : [],
    [selectedQuickFilterValue],
  );

  const defaultQuickFilters = useCallback(
    () =>
      [
        {
          order: 0,
          field: 'status',
          label: t('field.status', { ns: ALL_LNGS_NAMESPACES.APPLICATION }),
          options: [],
          searchMode: 'server',
        },
      ] as DefaultQuickFilter[],
    [t],
  );
  const getLabelQuickFilterLabel = useCallback(
    (name: string) => `${props?.labelPrefix ?? ''}${name}`,
    [props?.labelPrefix],
  );

  const { filterRefApi, isLoadingQuickFilters, fields } = useQuickFilters({
    defaultQuickFilters: defaultQuickFilters(),
    getLabel: getLabelQuickFilterLabel,
  });

  const overrideDefaultFilterTypes = useCallback((field: string, currentType: DatagridQuickFilterTypeEnum) => {
    if (field === 'status' && currentType === DatagridQuickFilterTypeEnum.AutoComplete) {
      return DatagridQuickFilterTypeEnum.SingleSelect;
    }
    return currentType;
  }, []);

  const quickFilterCall = useCallback(async () => {
    const statuses = Object.values(ApplicationStatusEnum);
    return Promise.resolve(
      statuses.map((status) => {
        const statusLabel = toString(status).toLowerCase();
        return {
          label: t(`status.${statusLabel}`, { ns: ALL_LNGS_NAMESPACES.APPLICATION }),
          value: status ?? 'failed',
        };
      }) ?? [],
    );
  }, [t]);

  const checkAllowReset = useCallback((filter?: string) => filter !== 'status', []);

  const handleQuickFilterChange = useCallback((quickFilters: DatagridQuickFilter[]) => {
    if (isEmpty(quickFilters)) {
      setSelectedQuickFilterValue(undefined);
    } else {
      const [filter] = quickFilters;
      const { input } = filter as DataGridSimpleQuickFilter;
      const { criteria } = input;
      setSelectedQuickFilterValue((criteria as string[])[0]);
    }
  }, []);

  const quickFiltersSearch = useMemo(
    () => ({
      filterRefApi,
      isLoadingQuickFilters,
      fields,
      getLabelQuickFilterLabel,
      overrideDefaultFilterTypes,
      quickFilterCall,
      checkAllowReset,
      handleQuickFilterChange,
      searchVersionQuickFilter,
    }),
    [
      filterRefApi,
      isLoadingQuickFilters,
      fields,
      getLabelQuickFilterLabel,
      overrideDefaultFilterTypes,
      quickFilterCall,
      checkAllowReset,
      handleQuickFilterChange,
      searchVersionQuickFilter,
    ],
  );

  return {
    ...quickFiltersSearch,
  };
}
